import { Component, Input } from '@angular/core'
import { sweetAlert } from '../../../bmAlert'
import { IKeyword, ICustomer, IContentType } from '../../../graphql/queries/keyword.queries'
import { CustomerActions } from '../../../actions/customer-actions'
import { KeywordMutations } from '../../../graphql/mutations/keyword.mutations'
import {
  updateCustomersNavRequestsCount,
  assignedWithoutDeadline,
  allowedContentTypes,
  multiplePostsFulfilled,
  hasViableDeadline,
  hasPostLimitAndPostCredits,
  isNotDeclined,
  hasPostLimit,
} from '../utils'
import { Keyword } from 'types'
import { successToast, errorToast } from 'src/toaster'
import { DeclineSubmissionsHelper } from '../helpers/decline-submissions-alerts.helper'
import pluralize from 'pluralize'

@Component({
  selector: 'request-buttons',
  template: `
    <div *ngIf="customer && keyword">
      <button
        mat-button
        [matMenuTriggerFor]="requestMenu"
        [disableRipple]="true"
        class="min-w-0 px-0">
        <i class="fas fa-ellipsis-v" [style.width]="'30px'"></i>
      </button>
      <mat-menu #requestMenu="matMenu">
        <button mat-menu-item *ngIf="isEditable(keyword)" [routerLink]="['./', keyword.id, 'edit']">
          View/Edit request
        </button>
        <div [popover]="activateButtonTooltipText" triggers="mouseenter:mouseout">
          <button
            mat-menu-item
            *ngIf="showActiveButton"
            [disabled]="!canReactivateKeyword"
            [class.disabled]="!canReactivateKeyword"
            (click)="reactivateKeyword(keyword)"
            id="activate-request">
            Activate request
          </button>
        </div>
        <button
          mat-menu-item
          *ngIf="!isEditable(keyword)"
          [routerLink]="['./', keyword.id, 'edit']">
          View request
        </button>
        <button
          mat-menu-item
          [routerLink]="['new', keyword.contentType.id]"
          [queryParams]="{ dupId: keyword.id }">
          Duplicate request
        </button>
        <button
          mat-menu-item
          *ngIf="['active', 'assigned'].includes(keyword.state)"
          (click)="deactivateKeyword()">
          Deactivate request
        </button>
        <button mat-menu-item *ngIf="keyword.state !== 'deleted'" (click)="deleteKeyword()">
          Delete request
        </button>
        <div [popover]="viewSubmissionsTooltipText" triggers="mouseenter:mouseout">
          <button
            mat-menu-item
            [disabled]="keyword.postsCount === 0"
            [routerLink]="['/dashboard', this.customer.id, 'submissions']"
            [queryParams]="{ search: this.keyword.id, title: this.keyword.phrase }">
            View submissions
          </button>
        </div>
      </mat-menu>
    </div>
  `,
})
export class RequestButtons {
  @Input() public customerId: number
  @Input() public customer: ICustomer
  @Input() public contentTypes: IContentType[]
  @Input() public keyword: IKeyword

  constructor(
    private customerActions: CustomerActions,
    private keywordMutations: KeywordMutations
  ) {}

  isEditable(request: IKeyword): boolean {
    return !['draft', 'deleted', 'fulfilled'].includes(request.state)
  }

  deleteKeyword(): void {
    const creditsCount = this.keyword.reservedPostCreditsCount
    let message: string

    if (creditsCount > 0) {
      message = `You'll get back ${creditsCount} ${pluralize('credit', creditsCount)}.`
    } else {
      message = 'Are you sure?'
    }

    sweetAlert.confirm({ title: 'Delete Request?', text: message }).then((confirm) => {
      if (confirm) {
        const id: number = this.keyword.id
        const customerId: number = this.customerId
        this.keywordMutations.deleteKeyword({ customerId, id }).then((data) => {
          // Can be removed after using Graphql for left nav customers
          if (data) updateCustomersNavRequestsCount(data, this.customerActions)
        })
      }
    })
  }

  async deactivateKeyword(): Promise<void> {
    const cancelConfirm = await DeclineSubmissionsHelper.showDeactivateRequestSweetAlert(
      this.keyword.reservedPostCreditsCount,
      this.keyword.multipassCreditReserved
    )
    if (cancelConfirm && this.keyword.queuedCount > 0) {
      const confirmDeclineSubmissions =
        await DeclineSubmissionsHelper.showDeclineInactiveSubmissionsSweetAlert(
          this.keyword.queuedCount
        )
      if (confirmDeclineSubmissions) {
        this.keywordMutations.declineRemainingKeywordSubmissions({
          id: this.keyword.id,
        })
      }
    }
    if (cancelConfirm) this.mutateKeywordDeactivation()
  }

  get canReactivateKeyword(): boolean {
    if (this.customer.multipass) {
      return false
    } else {
      return (
        allowedContentTypes(this.contentTypes, this.customer.plan.words).some(
          (type) => type.id === this.keyword.contentType.id
        ) &&
        hasPostLimit(this.keyword) &&
        hasPostLimitAndPostCredits(this.customer, this.keyword) &&
        isNotDeclined(this.keyword) &&
        !assignedWithoutDeadline(this.keyword) &&
        !multiplePostsFulfilled(this.keyword) &&
        hasViableDeadline(this.keyword, this.customer.deadlineBufferHours)
      )
    }
  }

  get activateButtonTooltipText(): string {
    let message = ''

    // This 1st conditional only affects requests without post_limit, soon this won't be needed (some on canReactivateKeyword)
    if (this.customer.multipass) {
      message = 'To activate, please click on the item to edit it.'
    } else if (!hasPostLimit(this.keyword)) {
      message = 'Click ‘View/Edit’ to activate this request.'
    } else if (!hasPostLimitAndPostCredits(this.customer, this.keyword)) {
      message =
        'You don’t have enough credits to activate this request. Click ‘View/Edit’ to use a one-time credit card charge instead.'
    } else if (assignedWithoutDeadline(this.keyword)) {
      message = 'Add a deadline for the assigned writer to activate this request.'
    } else if (!isNotDeclined(this.keyword)) {
      message =
        'Your assigned writer declined this request. Please change or remove the assigned writer.'
    } else if (!hasViableDeadline(this.keyword, this.customer.deadlineBufferHours)) {
      message = 'Update the deadline to activate this request.'
    } else if (multiplePostsFulfilled(this.keyword)) {
      message = 'To request multiple submissions, click ‘View/Edit’ to activate this request.'
    }
    return message
  }

  get viewSubmissionsTooltipText(): string {
    return this.keyword.postsCount > 0 ? '' : "There aren't any submissions on this request yet"
  }

  get showActiveButton(): boolean {
    return ['draft', 'paused', 'declined', 'auto_declined'].includes(this.keyword.state)
  }

  reactivateKeyword(keyword: IKeyword): void {
    if (this.canReactivateKeyword) {
      const updates: Partial<Keyword> = {
        state: keyword.writer ? 'assigned' : 'active',
        quantityFromCredits: keyword.postLimit,
      }
      this.keywordMutations
        .updateKeyword(
          {
            customerId: this.customerId,
            id: keyword.id,
            updates: updates,
          },
          this.customer.multipass
        )
        .then(() => {
          successToast('Request activated')
        })
        .catch(() => {
          errorToast("Couldn't activate keyword.")
        })
    }
  }

  async declineRemainingKeywordSubmissions(): Promise<void> {
    const confirmDeclineSubmissions =
      await DeclineSubmissionsHelper.showDeclineInactiveSubmissionsSweetAlert(
        this.keyword.queuedCount
      )
    if (confirmDeclineSubmissions)
      await this.keywordMutations.declineRemainingKeywordSubmissions({
        id: this.keyword.id,
      })
  }

  preventExpansion(event: Event): void {
    event.stopPropagation()
  }

  private async mutateKeywordDeactivation(): Promise<any> {
    const updateValues = {
      id: this.keyword.id,
      customerId: this.customerId,
    }
    const updates: object = {
      state: 'paused',
      product: {
        id: this.keyword.product.id,
        addOns: this.keyword.product.addOns.map((addOn) => ({
          id: addOn.id,
          quantity: addOn.quantity,
        })),
      },
    }
    const mutateDeactivateResponse = await this.keywordMutations.updateKeyword(
      { ...updateValues, updates: updates },
      this.customer.multipass
    )
    if (mutateDeactivateResponse)
      updateCustomersNavRequestsCount(mutateDeactivateResponse, this.customerActions)
  }
}
